"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { MockUser } from "@/types/index";
import { useGroupStore } from "@/store/groupStore";
import { useUserStore } from "@/store/userStore";
import { fetchStrapiCollection } from "@/lib/strapi";
import { ACTIVE_USER_FILTERS } from "@/lib/active-user-filter";

const baseUrl = '/api/strapi';

// How often AuthContext re-validates the session (and, since MWBI-913,
// self-heals a stale role claim) while a tab stays open and foregrounded.
// Widened from 5 min; tab-focus (visibilitychange, below) covers the common
// "left and came back" case, so this ceiling only matters for a
// continuously-idle-but-visible tab. No jitter — there's no server-side
// batch job here, just independent per-browser polling, so a thundering
// herd isn't a concern (see MWBI-913 plan).
const SESSION_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

interface SignInCredentials {
  email: string;
  password: string;
}

interface SignUpData {
  email: string;
  password: string;
  displayName: string;
  location?: string;
  avatarFile?: File;
  ageGroup?: "age_18_plus" | "under_18";
  birthdate?: string;
}

interface AuthContextType {
  currentUser: MockUser | null;
  users: MockUser[];
  signIn: (credentials: SignInCredentials) => Promise<{ success: boolean; error?: string }>;
  signUp: (data: SignUpData) => Promise<{ success: boolean; error?: string; requiresVerification?: boolean; email?: string }>;
  verify: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  selectUser: (userId: number | null) => Promise<void>;
  updateUser: (userId: number, data: Partial<MockUser>) => Promise<{ success: boolean; error?: string }>;
  updateNotificationPreference: (key: string, value: boolean) => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  patchCurrentUser: (patch: Partial<MockUser>) => void;
  toggleFollowGroup: (groupDocumentId: string) => Promise<void>;
  toggleFavoriteGroup: (groupDocumentId: string) => Promise<{ success: boolean; error?: string }>;
  isFollowingGroup: (groupDocumentId: string) => boolean;
  isFavoriteGroup: (groupDocumentId: string) => boolean;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<MockUser | null>(null);
  const [users, setUsers] = useState<MockUser[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followingGroupIds, setFollowingGroupIds] = useState<Set<string>>(new Set());
  const [favoriteGroupIds, setFavoriteGroupIds] = useState<Set<string>>(new Set());

  // Raw shape returned by the Strapi users API
  interface StrapiUserResponse {
    id: number;
    profileUuid?: string;
    cognitoSub?: string;
    username: string;
    displayName?: string;
    email: string;
    initials?: string;
    avatarColor?: string;
    avatarUrl?: string;
    location?: string;
    bio?: string;
    allowContact?: boolean;
    notifyForumReplies?: boolean;
    notifyGroupActivity?: boolean;
    notifyWeeklyDigest?: boolean;
    notifyDailySummary?: boolean;
    notifyGroupPostComments?: boolean;
    notifyGroupMemberRequests?: boolean;
    providerNumber?: string;
    providerVerified?: boolean;
    providerProfile?: { id: number };
    role?: { id: number; type: 'authenticated' | 'blog_writer' | 'admin' | 'superadmin' };
    ageGroup?: "age_18_plus" | "under_18" | "age_16_plus" | "age_13_to_15";
    birthdate?: string;
    showGroups?: boolean;
    acceptedCommunityGuidelinesAt?: string | null;
    ageVerifiedAt?: string | null;
  }

  // Map API response to MockUser
  const mapToMockUser = useCallback((user: StrapiUserResponse): MockUser => ({
    id: user.id,
    profileUuid: user.profileUuid,
    cognitoSub: user.cognitoSub,
    username: user.username,
    displayName: user.displayName || user.username,
    email: user.email,
    initials: user.initials || user.username?.substring(0, 2).toUpperCase(),
    avatarColor: user.avatarColor || '#00423E',
    avatarUrl: user.avatarUrl,
    location: user.location,
    bio: user.bio,
    allowContact: user.allowContact,
    notifyForumReplies: user.notifyForumReplies,
    notifyGroupActivity: user.notifyGroupActivity,
    notifyWeeklyDigest: user.notifyWeeklyDigest,
    notifyDailySummary: user.notifyDailySummary,
    notifyGroupPostComments: user.notifyGroupPostComments,
    notifyGroupMemberRequests: user.notifyGroupMemberRequests,
    providerNumber: user.providerNumber,
    providerVerified: user.providerVerified,
    providerProfileId: user.providerProfile?.id,
    role: user.role,
    ageGroup: user.ageGroup,
    birthdate: user.birthdate,
    showGroups: user.showGroups ?? true,
    acceptedCommunityGuidelinesAt: user.acceptedCommunityGuidelinesAt ?? null,
    // Null means the member has never answered the age question, which is what
    // opens the blocking age modal (MWBI-899). Note this is NOT the same as
    // "ageGroup is null" -- Google signups used to be recorded as age_18_plus
    // without anyone being asked, so ageGroup cannot stand in for an answer.
    ageVerifiedAt: user.ageVerifiedAt ?? null,
  }), []);

  // Bumped by clearAuthState(). fetchUsers() drops any result that lands after
  // a bump, so a directory request still in flight at sign-out cannot refill
  // the list afterwards (#1473).
  const directoryGenerationRef = useRef(0);

  // Fetch users from Strapi (for user listing features)
  const fetchUsers = useCallback(async () => {
    const generation = directoryGenerationRef.current;
    try {
      const rawUsers = await fetchStrapiCollection<Parameters<typeof mapToMockUser>[0]>('users', {
        // Active members only: not deleted, blocked, or archived (MWBI-896).
        filters: ACTIVE_USER_FILTERS,
        populate: 'role',
      });
      if (generation !== directoryGenerationRef.current) return [];
      const mappedUsers: MockUser[] = rawUsers.map(mapToMockUser);
      setUsers(mappedUsers);
      return mappedUsers;
    } catch (error) {
      if (generation !== directoryGenerationRef.current) {
        // The session that asked for this has ended, so nothing will read the
        // result. Warn, not error: patchConsoleErrorForRum (lib/rum.ts)
        // forwards console.error, not console.warn, to RUM as a JS error.
        console.warn('Dropped a user directory error from an ended session:', error);
        return [];
      }
      console.error('Error fetching users:', error);
      return [];
    }
  }, [mapToMockUser]);

  // Check session on mount via /api/auth/me
  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch('/api/auth/me', {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            const userData = mapToMockUser(data.data);
            setCurrentUser(userData);

            // Populate the user directory in the background — do NOT await it.
            // Awaiting here gated `setLoading(false)` / `setIsHydrated(true)`
            // (below) behind a whole-directory fetch, so the app sat on a blank
            // screen until it finished. `fetchUsers` sets `users` itself and
            // swallows its own errors, so a fire-and-forget call is safe.
            // Near-term piece of MWBI-761 (shrink the AuthContext load).
            void fetchUsers();

            // Hydrate user action Sets
            try {
              const statusRes = await fetch(`${baseUrl}/user-actions/status`);
              if (statusRes.ok) {
                const statusData = await statusRes.json();
                setFollowingGroupIds(new Set(statusData.data.followingGroupIds));
                setFavoriteGroupIds(new Set(statusData.data.favoriteGroupIds));
              }
            } catch (err) {
              console.error('Error loading user action status:', err);
            }
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }

      setLoading(false);
      setIsHydrated(true);
    };
    init();
  }, [fetchUsers, mapToMockUser]);

  // Clear auth state helper. Every place this provider ends a session calls
  // it: signOut, selectUser(null), and the visibility and periodic checks.
  const clearAuthState = useCallback(() => {
    directoryGenerationRef.current += 1;
    setCurrentUser(null);
    // The member directory is for signed-in members only (#1473). Drop both
    // copies, this context's and the user store's, so no signed-out page can
    // keep rendering it.
    setUsers([]);
    useUserStore.getState().clearUsers();
    setFollowingGroupIds(new Set());
    setFavoriteGroupIds(new Set());
    useGroupStore.setState({ members: [], membersLoaded: false, myMemberships: [], myMembershipsLoaded: false, groups: [], groupsLoaded: false });
  }, []);

  // Re-validate session when the tab becomes visible (user returns after being away)
  useEffect(() => {
    if (!currentUser) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            setCurrentUser(mapToMockUser(data.data));
          }
        } else {
          clearAuthState();
        }
      } catch (_error) { // eslint-disable-line no-restricted-syntax -- visibility-change session check; periodic interval will retry
        // Network error — don't clear state, might be temporary
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUser, mapToMockUser, clearAuthState]);

  // Periodic session check — catch expired sessions while the tab is active
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (!res.ok) {
          clearAuthState();
        }
      } catch (_error) { // eslint-disable-line no-restricted-syntax -- periodic session check; persisted session is the fallback
        // Network error — don't clear state
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [currentUser, clearAuthState]);

  // Sign in with email and password
  const signIn = async (credentials: SignInCredentials): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/auth/signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        return { success: false, error: data.error || 'Invalid email or password' };
      }

      // Always hydrate from /api/auth/me after sign-in to avoid partial user payload drift.
      let userData: MockUser;
      try {
        const meRes = await fetch('/api/auth/me', { credentials: 'include' });
        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData.success && meData.data) {
            userData = mapToMockUser(meData.data);
          } else {
            userData = mapToMockUser(data.data);
          }
        } else {
          userData = mapToMockUser(data.data);
        }
      } catch (error) {
        console.error('src/context/AuthContext/AuthContext.tsx', error);
        userData = mapToMockUser(data.data);
      }
      setCurrentUser(userData);

      // Hydrate user action Sets
      try {
        const statusRes = await fetch(`${baseUrl}/user-actions/status`);
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setFollowingGroupIds(new Set(statusData.data.followingGroupIds));
          setFavoriteGroupIds(new Set(statusData.data.favoriteGroupIds));
        }
      } catch (err) {
        console.error('Error loading user action status:', err);
      }

      await fetchUsers(); // Refresh users list
      return { success: true };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Sign up new user
  const signUp = async (data: SignUpData): Promise<{ success: boolean; error?: string; requiresVerification?: boolean; email?: string }> => {
    try {
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('displayName', data.displayName);
      if (data.location) formData.append('location', data.location);
      if (data.avatarFile) formData.append('avatar', data.avatarFile);
      if (data.ageGroup) formData.append('ageGroup', data.ageGroup);
      if (data.birthdate) formData.append('birthdate', data.birthdate);

      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        body: formData,
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        return { success: false, error: result.error || 'Failed to create account' };
      }

      return {
        success: true,
        requiresVerification: result.requiresVerification,
        email: data.email
      };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Verify OTP for the post-signup email confirmation. The Phase B verify
  // route returns the user record and (server-side) sets strapi_jwt cookie.
  // We mirror signIn's "hydrate from /api/auth/me after success" pattern so
  // currentUser stays the canonical source of truth and the soft-nav from
  // /verify-email -> / picks up the new session without a hard reload.
  const verify = async (email: string, code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        return { success: false, error: data.error || 'Verification failed' };
      }

      // Hydrate currentUser from /api/auth/me so all downstream consumers
      // see the fresh session immediately. Falls back to the user object in
      // the verify response if /me hiccups (e.g. cookie not yet readable).
      let userData: MockUser;
      try {
        const meRes = await fetch('/api/auth/me', { credentials: 'include' });
        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData.success && meData.data) {
            userData = mapToMockUser(meData.data);
          } else {
            userData = mapToMockUser(data.user);
          }
        } else {
          userData = mapToMockUser(data.user);
        }
      } catch (err) {
        console.error('verify -> /api/auth/me failed:', err);
        userData = mapToMockUser(data.user);
      }
      setCurrentUser(userData);

      try {
        const statusRes = await fetch(`${baseUrl}/user-actions/status`);
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setFollowingGroupIds(new Set(statusData.data.followingGroupIds));
          setFavoriteGroupIds(new Set(statusData.data.favoriteGroupIds));
        }
      } catch (err) {
        console.error('verify -> user-actions/status failed:', err);
      }

      await fetchUsers();
      return { success: true };
    } catch (error) {
      console.error('Verify error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Sign out
  const signOut = async (): Promise<void> => {
    try {
      await fetch('/api/auth/signout', { method: 'POST' });
    } catch (error) {
      console.error('Sign out error:', error);
    }
    clearAuthState();
  };

  const selectUser = async (userId: number | null) => {
    if (userId === null) {
      try {
        await fetch('/api/auth/signout', { method: 'POST' });
      } catch (error) {
        console.error('src/context/AuthContext/AuthContext.tsx', error);
        // best-effort
      }
      clearAuthState();
      return;
    }

    const user = users.find((u) => u.id === userId);
    if (!user) return;
    setCurrentUser(user);
  };

  const updateUser = async (userId: number, data: Partial<MockUser>): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(`${baseUrl}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const errorData = await res.json();
        // Check for unique constraint violation
        if (errorData.error?.message?.includes('username') || errorData.error?.details?.errors?.some((e: { path?: string[] }) => e.path?.includes('username'))) {
          return { success: false, error: 'This username is already taken. Please choose another one.' };
        }
        return { success: false, error: errorData.error?.message || 'Failed to update profile' };
      }

      const updatedUser = await res.json();

      // Update local state
      const mappedUser: MockUser = {
        id: updatedUser.id,
        profileUuid: updatedUser.profileUuid,
        cognitoSub: updatedUser.cognitoSub,
        username: updatedUser.username,
        displayName: updatedUser.displayName || updatedUser.username,
        email: updatedUser.email,
        initials: updatedUser.initials || updatedUser.username.substring(0, 2).toUpperCase(),
        avatarColor: updatedUser.avatarColor || '#00423E',
        avatarUrl: updatedUser.avatarUrl,
        location: updatedUser.location,
        bio: updatedUser.bio,
        allowContact: updatedUser.allowContact,
        notifyForumReplies: updatedUser.notifyForumReplies,
        notifyGroupActivity: updatedUser.notifyGroupActivity,
        notifyWeeklyDigest: updatedUser.notifyWeeklyDigest,
        notifyDailySummary: updatedUser.notifyDailySummary,
        notifyGroupPostComments: updatedUser.notifyGroupPostComments,
        notifyGroupMemberRequests: updatedUser.notifyGroupMemberRequests,
        providerNumber: updatedUser.providerNumber,
        providerVerified: updatedUser.providerVerified,
        providerProfileId: updatedUser.providerProfile?.id,
        role: updatedUser.role,
        ageGroup: updatedUser.ageGroup,
        birthdate: updatedUser.birthdate,
        showGroups: updatedUser.showGroups ?? true,
        acceptedCommunityGuidelinesAt: updatedUser.acceptedCommunityGuidelinesAt ?? null,
        // Never let a profile update clear the age latch locally. verify-age is
        // write-once, so re-opening the modal would strand the member behind a
        // blocking dialog whose answer the server refuses. If the update response
        // omits the field, keep what we already hold for that same member (and
        // only for them -- another row in the users list must not inherit it).
        ageVerifiedAt:
          updatedUser.ageVerifiedAt
          ?? (currentUser?.id === userId ? currentUser.ageVerifiedAt ?? null : null),
      };

      // Update users list
      setUsers(prevUsers => prevUsers.map(u => u.id === userId ? mappedUser : u));

      // Update current user if it's the one being edited
      if (currentUser?.id === userId) {
        setCurrentUser(mappedUser);
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const updateNotificationPreference = async (key: string, value: boolean) => {
    if (!currentUser) return;

    try {
      const res = await fetch(`${baseUrl}/users/${currentUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [key]: value }),
      });

      if (!res.ok) {
        console.error('Failed to update notification preference');
        return;
      }

      const _updatedUser = await res.json();

      // Update local state
      const mappedUser: MockUser = {
        ...currentUser,
        [key]: value,
      };

      // Update users list
      setUsers(prevUsers => prevUsers.map(u => u.id === currentUser.id ? mappedUser : u));

      // Update current user
      setCurrentUser(mappedUser);
    } catch (error) {
      console.error('Error updating notification preference:', error);
    }
  };

  const patchCurrentUser = useCallback((patch: Partial<MockUser>): void => {
    setCurrentUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const refreshCurrentUser = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && data.data) {
        setCurrentUser(mapToMockUser(data.data));
      }
    } catch (error) {
      console.error('Error refreshing current user:', error);
    }
  }, [mapToMockUser]);

  const isFollowingGroup = useCallback((groupDocumentId: string): boolean => {
    return followingGroupIds.has(groupDocumentId);
  }, [followingGroupIds]);

  const isFavoriteGroup = useCallback((groupDocumentId: string): boolean => {
    return favoriteGroupIds.has(groupDocumentId);
  }, [favoriteGroupIds]);

  const toggleFollowGroup = async (groupDocumentId: string) => {
    if (!currentUser) {
      // Modal will be opened by the calling component using useAuthModal hook
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/user-actions/toggle-follow-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupDocumentId }),
      });
      if (!res.ok) throw new Error('Failed to toggle follow group');
      const { data } = await res.json();
      setFollowingGroupIds(prev => {
        const next = new Set(prev);
        if (data.following) next.add(groupDocumentId);
        else next.delete(groupDocumentId);
        return next;
      });
    } catch (err) {
      console.error('Error toggling follow group:', err);
    }
  };

  const toggleFavoriteGroup = async (groupDocumentId: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${baseUrl}/user-actions/toggle-favorite-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupDocumentId }),
      });
      if (!res.ok) throw new Error('Failed to toggle favorite group');
      const { data } = await res.json();
      setFavoriteGroupIds(prev => {
        const next = new Set(prev);
        if (data.favorited) next.add(groupDocumentId);
        else next.delete(groupDocumentId);
        return next;
      });
      return { success: true };
    } catch (err) {
      console.error('Error toggling favorite group:', err);
      return { success: false, error: 'Failed to save favorite' };
    }
  };

  // Prevent hydration mismatch
  if (!isHydrated) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        users,
        signIn,
        signUp,
        verify,
        signOut,
        selectUser,
        updateUser,
        updateNotificationPreference,
        refreshCurrentUser,
        patchCurrentUser,
        toggleFollowGroup,
        toggleFavoriteGroup,
        isFollowingGroup,
        isFavoriteGroup,
        isAuthenticated: currentUser !== null,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

